import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  PdfLoader,
  PdfHighlighter,
  usePdfHighlighterContext,
  useHighlightContainerContext,
  TextHighlight,
} from 'react-pdf-highlighter-extended';
import { Spin, Button, Input, Modal, Tooltip } from 'antd';
import {
  CheckOutlined, LeftOutlined, RightOutlined,
  EditOutlined, StopOutlined, ExclamationCircleOutlined,
  SearchOutlined, UpOutlined, DownOutlined,
  CheckCircleOutlined, CloseOutlined, FilePdfOutlined,
  InfoCircleOutlined, UndoOutlined, SendOutlined, MessageOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { C, W, REVIEW_STATUS, HccBadge, GOSPEL_SECTIONS, ReviewStatusDot, SupportLabel } from './HCCReviewConstants';

// ── P1: Encounter list ────────────────────────────────────────────────────────
export const EncounterList = ({ encounters, selectedEncId, onSelect, loading }) => (
  <div style={{
    width: W.p1, flexShrink: 0,
    background: C.navy,
    display: 'flex', flexDirection: 'column', overflowY: 'auto',
  }}>

    {loading ? (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <Spin size="small" style={{ filter: 'brightness(10)' }} />
      </div>
    ) : encounters.map(enc => {
      const isSelected = enc.encounter_id === selectedEncId;
      const hasDate = enc.encounter_date || enc.dos;
      return (
        <div
          key={enc.encounter_id}
          onClick={() => onSelect(enc)}
          data-telemetry-label={`select_encounter:${enc.encounter_id}`}
          style={{
            margin: '5px 8px',
            padding: '10px 12px',
            borderRadius: 8,
            cursor: 'pointer',
            background: isSelected ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.06)',
            border: isSelected ? '1px solid rgba(255,255,255,0.25)' : '1px solid transparent',
            transition: 'background 0.15s, border 0.15s',
          }}
          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.11)'; }}
          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: 'white' }}>
              ENC {String(enc.encounter_number).padStart(2, '0')}
            </span>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
              pp.{enc.start_page}–{enc.end_page}
            </span>
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 5 }}>
            {hasDate || 'No date'}
          </div>
          {enc.description && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.45, marginBottom: 6 }}>
              {enc.description}
            </div>
          )}
          <span style={{
            display: 'inline-block', fontSize: 12, fontWeight: 700,
            color: enc.coded_findings_count > 0 ? C.navy : 'rgba(255,255,255,0.4)',
            background: enc.coded_findings_count > 0 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.1)',
            padding: '2px 8px', borderRadius: 9999,
          }}>
            {enc.coded_findings_count} code{enc.coded_findings_count !== 1 ? 's' : ''}
          </span>
        </div>
      );
    })}
  </div>
);


// ── P2: Code list ─────────────────────────────────────────────────────────────
export const CodeList = ({
  findings, selectedFindingId, decisions,
  onSelectFinding, loading,
  activeEvidenceIdx, onEvidenceClick,
}) => {
  const [showAllEvidence, setShowAllEvidence] = React.useState({});
  return (
    <div style={{
      flex: 1, minWidth: 0,
      background: C.white,
      display: 'flex', flexDirection: 'column', overflowY: 'auto',
    }}>


      {loading ? (
        <div style={{ padding: 20, textAlign: 'center' }}><Spin size="small" /></div>
      ) : findings.length === 0 ? (
        <div style={{ padding: 16, color: C.textMuted, fontSize: 16, textAlign: 'center' }}>
          No codes for this encounter
        </div>
      ) : (
        findings.map(f => {
          const isSelected = f.finding_id === selectedFindingId;
          const dec = decisions[f.finding_id]?.decision || 'pending';
          const isActioned = dec !== 'pending' && dec !== 'reverted' && dec !== 'reset' && dec !== 'recalled';
          const borderColor = isSelected ? C.navy : isActioned ? (REVIEW_STATUS[dec]?.color || C.border) : 'transparent';
          return (
            <div
              key={f.finding_id}
              style={{
                padding: '10px 12px',
                borderBottom: `1px solid ${C.border}`,
                borderLeft: `3px solid ${borderColor}`,
                background: isSelected ? C.navyLight : isActioned ? '#FAFBFF' : 'transparent',
                cursor: 'pointer',
                transition: 'background 0.1s',
              }}
              onClick={() => onSelectFinding(f)}
              data-telemetry-label="select_finding" data-telemetry-finding-id={f.finding_id}
              onMouseEnter={e => { if (!isSelected && !isActioned) e.currentTarget.style.background = '#F8FAFC'; }}
              onMouseLeave={e => { if (!isSelected && !isActioned) e.currentTarget.style.background = isActioned ? '#FAFBFF' : 'transparent'; }}
            >
              {/* Code + review status dot + HCC badges */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                <ReviewStatusDot decision={dec} />
                {dec === 'modified' && decisions[f.finding_id]?.originalCode ? (
                  <>
                    <span style={{ fontSize: 14, fontFamily: 'monospace', color: '#94A3B8', textDecoration: 'line-through' }}>
                      {decisions[f.finding_id].originalCode}
                    </span>
                    <span style={{ color: C.textMuted, fontSize: 12 }}>→</span>
                    <span style={{ fontSize: 16, fontWeight: 700, fontFamily: 'monospace', color: '#1D4ED8' }}>
                      {decisions[f.finding_id].newCode}
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: 16, fontWeight: 700, fontFamily: 'monospace', color: C.navy }}>
                    {f.icd10_code}
                  </span>
                )}
                <HccBadge mapping={f.hcc_mapping} />
              </div>

              {/* Description */}
              <div style={{
                fontSize: 15, color: C.text, lineHeight: 1.35,
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical', marginBottom: 4,
              }}>
                {dec === 'modified' && decisions[f.finding_id]?.newDescription
                  ? decisions[f.finding_id].newDescription
                  : f.description}
              </div>

              {/* Coder-added badge */}
              {f.source === 'coder' && (
                <div style={{ marginBottom: 4 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 6px',
                    borderRadius: 3, background: '#F0FDF4',
                    border: '1px solid #86EFAC', color: C.accepted,
                    letterSpacing: '0.04em',
                  }}>
                    ✦ CODER ADDED
                  </span>
                </div>
              )}

              {/* Section + meat + support status text */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {f.section && (
                  <span style={{ fontSize: 13, color: C.textMuted, background: '#F1F5F9', padding: '1px 5px', borderRadius: 3 }}>
                    {f.section}
                  </span>
                )}
                {f.meat_category && (
                  <span style={{ fontSize: 13, color: C.textMuted, background: '#F1F5F9', padding: '1px 5px', borderRadius: 3 }}>
                    {f.meat_category}
                  </span>
                )}
                <SupportLabel supportStatus={f.support_status} />
              </div>

              {/* Decision note preview (if any) */}
              {decisions[f.finding_id]?.note && (
                <div style={{
                  fontSize: 11, color: '#92400E', fontStyle: 'italic',
                  background: '#FFFBEB', padding: '3px 6px', borderRadius: 3,
                  marginBottom: 6, lineHeight: 1.4,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  💬 {decisions[f.finding_id].note}
                </div>
              )}

              {/* ── Expanded evidence (auto-shows for selected code) ── */}
              {isSelected && f.evidence?.length > 0 && (
                <div style={{ marginBottom: 4 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: C.textMuted,
                    letterSpacing: '0.08em', marginBottom: 5,
                  }}>
                    EVIDENCE &middot; {f.evidence.length} ITEM{f.evidence.length !== 1 ? 'S' : ''}
                  </div>

                  {(showAllEvidence[f.finding_id] ? f.evidence : f.evidence.slice(0, 3))
                    .map((ev, idx) => {
                      const isActiveEv = idx === activeEvidenceIdx;
                      return (
                        <div
                          key={idx}
                          onClick={e => { e.stopPropagation(); onEvidenceClick?.(idx); }}
                          style={{
                            padding: '5px 8px', marginBottom: 4, borderRadius: 4,
                            cursor: 'pointer',
                            background: isActiveEv ? C.navyLight : '#FAFAFA',
                            border: `1px solid ${isActiveEv ? C.navy : C.border}`,
                            borderLeft: `3px solid ${isActiveEv ? C.navy : 'transparent'}`,
                            transition: 'all 0.12s',
                          }}
                        >
                          <div style={{ display: 'flex', gap: 5, marginBottom: 3, alignItems: 'center' }}>
                            <span style={{
                              fontSize: 11, fontWeight: 700, fontFamily: 'monospace',
                              background: isActiveEv ? C.navy : '#64748B',
                              color: 'white', padding: '1px 5px', borderRadius: 3,
                            }}>
                              p.{ev.page}
                            </span>
                            <span style={{
                              fontSize: 10, color: C.textMuted,
                              background: '#F1F5F9', padding: '1px 5px', borderRadius: 3,
                              fontFamily: 'monospace',
                            }}>
                              {ev.section}
                            </span>
                          </div>
                          {(ev.context?.match || ev.entity_text) && (
                            <div style={{
                              fontSize: 11, lineHeight: 1.4, fontStyle: 'italic',
                              color: isActiveEv ? C.text : C.textMuted,
                            }}>
                              {(() => {
                                const t = ev.context?.match || ev.entity_text || '';
                                return `"${t.length > 70 ? t.slice(0, 70) + '…' : t}"`;
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })
                  }

                  {f.evidence.length > 3 && (
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setShowAllEvidence(prev => ({ ...prev, [f.finding_id]: !prev[f.finding_id] }));
                      }}
                      style={{
                        width: '100%', background: 'none',
                        border: `1px solid ${C.border}`, borderRadius: 4,
                        padding: '3px 0', fontSize: 11, color: C.textMuted,
                        cursor: 'pointer', marginBottom: 4,
                      }}
                    >
                      {showAllEvidence[f.finding_id] ? '▲ Show less' : `▼ +${f.evidence.length - 3} more`}
                    </button>
                  )}

                  {f.corroboration?.llm_reasoning && (
                    <div style={{
                      fontSize: 11, color: C.textMuted, fontStyle: 'italic',
                      lineHeight: 1.45, paddingTop: 5,
                      borderTop: `1px solid ${C.border}`, marginTop: 2,
                    }}>
                      {f.corroboration.llm_reasoning}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

// ── Page strip (used in both P3 and P4) ───────────────────────────────────────
export const PageStrip = ({ startPage, endPage, activePage, highlightPages, onPageClick, label }) => {
  const pages = [];
  for (let i = startPage; i <= endPage; i++) pages.push(i);
  const stripRef = useRef(null);

  // Auto-scroll active page into view
  useEffect(() => {
    if (!stripRef.current || !activePage) return;
    const btn = stripRef.current.querySelector(`[data-page="${activePage}"]`);
    if (btn) btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activePage]);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '6px 10px', background: '#F8FAFC',
      borderBottom: `1px solid ${C.border}`, flexShrink: 0,
      minHeight: 40,
    }}>
      {label && (
        <span style={{ fontSize: 14, fontWeight: 700, color: C.textMuted, whiteSpace: 'nowrap', marginRight: 4 }}>
          {label}
        </span>
      )}
      <div ref={stripRef} style={{ display: 'flex', gap: 3, overflowX: 'auto', flex: 1 }}>
        {pages.map(page => {
          const isActive  = page === activePage;
          const hasEvidence = highlightPages.includes(page);
          return (
            <button
              key={page}
              data-page={page}
              onClick={() => onPageClick(page)}
              style={{
                minWidth: 30, height: 26, borderRadius: 4, border: 'none',
                cursor: 'pointer', fontSize: 15,
                fontWeight: isActive ? 700 : hasEvidence ? 600 : 400,
                background: isActive ? C.navy : hasEvidence ? C.blueBg : '#F1F5F9',
                color: isActive ? 'white' : hasEvidence ? C.blue : C.textMuted,
                position: 'relative', flexShrink: 0,
                transition: 'all 0.1s',
              }}
            >
              {page}
              {hasEvidence && !isActive && (
                <span style={{
                  position: 'absolute', bottom: 2, left: '50%',
                  transform: 'translateX(-50%)',
                  width: 3, height: 3, borderRadius: '50%', background: C.blue,
                }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Evidence navigator strip (P4 top) ─────────────────────────────────────────
export const EvidenceNavigator = ({ items, activeIdx, onNavigate }) => {
  if (!items || items.length === 0) return null;
  const current = items[activeIdx];
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 12px', background: '#FFFBEB',
      borderBottom: `1px solid #FDE68A`, flexShrink: 0, minHeight: 40,
    }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: '#92400E', whiteSpace: 'nowrap' }}>
        SUPPORT
      </span>
      <span style={{ fontSize: 15, color: '#92400E' }}>
        {activeIdx + 1} / {items.length}
      </span>
      <button
        onClick={() => onNavigate(activeIdx - 1)}
        disabled={activeIdx === 0}
        style={{
          width: 22, height: 22, borderRadius: 4, border: `1px solid #FDE68A`,
          background: 'white', cursor: activeIdx === 0 ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: activeIdx === 0 ? 0.4 : 1,
        }}
      >
        <LeftOutlined style={{ fontSize: 13 }} />
      </button>
      <button
        onClick={() => onNavigate(activeIdx + 1)}
        disabled={activeIdx >= items.length - 1}
        data-telemetry-label="evidence_nav_next"
        style={{
          width: 22, height: 22, borderRadius: 4, border: `1px solid #FDE68A`,
          background: 'white', cursor: activeIdx >= items.length - 1 ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: activeIdx >= items.length - 1 ? 0.4 : 1,
        }}
      >
        <RightOutlined style={{ fontSize: 13 }} />
      </button>
      {current && (
        <span style={{ fontSize: 14, color: C.textMuted, marginLeft: 4 }}>
          p.{current.page} · {current.section || '—'}
        </span>
      )}
    </div>
  );
};

// ── Codes Accepted popup ──────────────────────────────────────────────────────
export const CodesAcceptedPopup = ({ acceptedFindings, encounters, onRevoke, onFinalize }) => {
  // Group by encounter_number — with fallback parsing from encounter_id
  const grouped = {};
  acceptedFindings.forEach(f => {
    let key = f.encounter_number;
    // Fallback: parse from encounter_id (e.g., "encounter_001_p0007_p0028" → 1)
    if (key === undefined || key === null) {
      const match = f.encounter_id?.match(/encounter_(\d+)/);
      key = match ? parseInt(match[1], 10) : 0;
    }
    const date = f.encounter_date || encounters?.find(e => e.encounter_id === f.encounter_id)?.encounter_date || '';
    if (!grouped[key]) grouped[key] = { number: key, date, findings: [] };
    grouped[key].findings.push(f);
  });
  const groups = Object.values(grouped).sort((a, b) => a.number - b.number);

  return (
    <div style={{
      width: 340, maxHeight: '60vh',
      background: 'white', borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '14px 16px 10px',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: C.navy }}>Codes Accepted</div>
        <div style={{ fontSize: 15, color: C.textMuted, marginTop: 2 }}>
          {acceptedFindings.length} code{acceptedFindings.length !== 1 ? 's' : ''} accepted
        </div>
      </div>

      <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
        {groups.length === 0 ? (
          <div style={{ padding: '20px 16px', color: C.textMuted, fontSize: 16, textAlign: 'center' }}>
            No codes accepted yet
          </div>
        ) : groups.map(group => (
          <div key={group.number} style={{ padding: '8px 16px' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 6 }}>
              Encounter {String(group.number).padStart(2, '0')}
              {group.date && (
                <span style={{ fontWeight: 400, color: C.textMuted, marginLeft: 6, fontSize: 15 }}>
                  {group.date}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {group.findings.map(f => (
                <div key={f.findingId} style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  background: C.acceptedBg, borderRadius: 4,
                  padding: '3px 8px 3px 10px', fontSize: 16, fontWeight: 600,
                  color: C.accepted,
                }}>
                  <span style={{ fontFamily: 'monospace' }}>{f.icd10_code}</span>
                  <button
                    onClick={() => onRevoke(f.findingId)}
                    data-telemetry-label="revoke_accepted_finding" data-telemetry-finding-id={f.findingId}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#DC2626', padding: 0, display: 'flex',
                      alignItems: 'center', fontSize: 16, marginLeft: 2,
                    }}
                  >
                    <CloseOutlined style={{ fontSize: 14, color: '#DC2626' }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.border}` }}>
        <button
          onClick={onFinalize}
          disabled={acceptedFindings.length === 0}
          data-telemetry-label="open_finalize_review"
          style={{
            width: '100%', padding: '8px 0',
            background: acceptedFindings.length === 0 ? '#E5E7EB' : C.navy,
            color: acceptedFindings.length === 0 ? C.textMuted : 'white',
            border: 'none', borderRadius: 6, fontSize: 17, fontWeight: 700,
            cursor: acceptedFindings.length === 0 ? 'not-allowed' : 'pointer',
            letterSpacing: '0.05em',
          }}
        >
          FINALIZE
        </button>
      </div>
    </div>
  );
};



// ── Decision Note Modal — for Modify, Reject, Review actions ─────────────────
export const DecisionNoteModal = ({ visible, action, finding, onConfirm, onCancel }) => {
  const [note, setNote] = React.useState('');
  const [newCode, setNewCode] = React.useState('');
  const [newDescription, setNewDescription] = React.useState('');

  React.useEffect(() => {
    if (visible) { setNote(''); setNewCode(''); setNewDescription(''); }
  }, [visible, action]);

  if (!finding) return null;

  const config = {
    modified:     { title: 'Modify Code',   color: '#1D4ED8', icon: <EditOutlined />,                placeholder: 'Reason for modification...', required: true },
    rejected:     { title: 'Reject Code',   color: '#DC2626', icon: <StopOutlined />,                placeholder: 'Reason for rejection...', required: true },
    under_review: { title: 'Send to Review',color: '#B45309', icon: <ExclamationCircleOutlined />,    placeholder: 'Reason for sending to review (optional)...', required: false },
  }[action] || {};

  const isModify = action === 'modified';
  const canConfirm = isModify
    ? (newCode.trim() && newDescription.trim() && note.trim())
    : (!config.required || note.trim());

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      width={580}
      centered
      footer={[
        <Button key="cancel" onClick={onCancel} data-telemetry-label="cancel_decision_note">Cancel</Button>,
        <Button
          key="confirm"
          type="primary"
          disabled={!canConfirm}
          onClick={() => onConfirm({
            note: note.trim() || null,
            ...(isModify ? { newCode: newCode.trim(), newDescription: newDescription.trim() } : {}),
          })}
          data-telemetry-label={`confirm_decision_note:${action}`} data-telemetry-finding-id={finding.finding_id}
          style={{ background: config.color, borderColor: config.color }}
        >
          {config.icon} Confirm
        </Button>,
      ]}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: config.color, fontSize: 18 }}>{config.icon}</span>
          <span style={{ fontSize: 18, fontWeight: 700, color: config.color }}>{config.title}</span>
        </div>
      }
    >
      {/* Current code info card */}
      <div style={{
        background: '#F8FAFC', borderRadius: 8, padding: '12px 16px',
        border: `1px solid ${C.border}`, marginBottom: 16,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: '0.06em', marginBottom: 6 }}>
          {isModify ? 'CURRENT CODE (will be replaced)' : 'CODE'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 20, fontWeight: 700, fontFamily: 'monospace', color: isModify ? '#94A3B8' : C.navy,
            textDecoration: isModify ? 'line-through' : 'none',
          }}>
            {finding.icd10_code}
          </span>
          <HccBadge mapping={finding.hcc_mapping} />
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
          {finding.section && (
            <span style={{ fontSize: 12, color: C.textMuted, background: '#F1F5F9', padding: '1px 6px', borderRadius: 3 }}>
              {finding.section}
            </span>
          )}
          {finding.meat_category && (
            <span style={{ fontSize: 12, color: C.textMuted, background: '#F1F5F9', padding: '1px 6px', borderRadius: 3 }}>
              {finding.meat_category}
            </span>
          )}
          <SupportLabel supportStatus={finding.support_status} hasSupport={(finding.corroboration?.supporting_evidence?.length || 0) > 0} />
        </div>
        <div style={{ fontSize: 15, color: C.text, lineHeight: 1.4 }}>
          {finding.description}
        </div>

        {/* Evidence summary */}
        {finding.evidence?.length > 0 && (
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: '0.06em', marginBottom: 6 }}>
              EVIDENCE · {finding.evidence.length} ITEM{finding.evidence.length !== 1 ? 'S' : ''}
            </div>
            {finding.evidence.slice(0, 4).map((ev, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'baseline', fontSize: 13, color: C.textMuted, marginBottom: 3 }}>
                <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', background: '#64748B', color: 'white', padding: '0px 5px', borderRadius: 3, flexShrink: 0 }}>
                  p.{ev.page}
                </span>
                <span style={{ fontSize: 11, background: '#F1F5F9', padding: '0px 4px', borderRadius: 2 }}>{ev.section}</span>
                <span style={{ fontSize: 12, fontStyle: 'italic', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  "{(ev.context?.match || ev.entity_text || '').slice(0, 60)}"
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Support summary */}
        {finding.corroboration?.supporting_evidence?.length > 0 && (
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: '0.06em', marginBottom: 4 }}>
              SUPPORT · {finding.corroboration.supporting_evidence.length} ITEM{finding.corroboration.supporting_evidence.length !== 1 ? 'S' : ''}
            </div>
            {finding.corroboration.supporting_evidence.slice(0, 3).map((s, i) => (
              <div key={i} style={{ fontSize: 12, color: C.textMuted, marginBottom: 2 }}>
                p.{s.page} · {s.evidence_type || s.type || 'support'} — {(s.text || s.context?.match || '').slice(0, 50)}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* NEW CODE INPUTS — only for modification */}
      {isModify && (
        <div style={{
          background: '#EFF6FF', borderRadius: 8, padding: '12px 16px',
          border: '1px solid #BFDBFE', marginBottom: 16,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1D4ED8', letterSpacing: '0.06em', marginBottom: 8 }}>
            REPLACEMENT CODE
          </div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <Input
              value={newCode}
              onChange={e => setNewCode(e.target.value)}
              placeholder="ICD-10 code (e.g., I48.11)"
              style={{ width: 180, fontSize: 16, fontFamily: 'monospace', fontWeight: 700 }}
              autoFocus
            />
          </div>
          <Input
            value={newDescription}
            onChange={e => setNewDescription(e.target.value)}
            placeholder="Code description (e.g., Longstanding persistent atrial fibrillation)"
            style={{ fontSize: 14 }}
          />
        </div>
      )}

      {/* Note textarea */}
      <Input.TextArea
        value={note}
        onChange={e => setNote(e.target.value)}
        placeholder={config.placeholder}
        rows={3}
        autoFocus={!isModify}
        style={{ fontSize: 15 }}
      />
    </Modal>
  );
};



// ── Show decision history modal ────────────────────────────────────────────────────────────────
export const DecisionHistoryModal = ({ visible, onClose, history, loading, originalCode }) => {
  const actionStyles = {
    accepted:     { color: '#15803D', icon: <CheckOutlined /> },
    modified:     { color: '#1D4ED8', icon: <EditOutlined /> },
    rejected:     { color: '#DC2626', icon: <StopOutlined /> },
    under_review: { color: '#B45309', icon: <ExclamationCircleOutlined /> },
    reverted:     { color: '#94A3B8', icon: <UndoOutlined /> },
    reset:        { color: '#94A3B8', icon: <UndoOutlined /> },
    recalled:     { color: '#94A3B8', icon: <UndoOutlined /> },
  };
  return (
    <Modal
      open={visible} onCancel={onClose} width={520} centered
      title={<span style={{ fontSize: 16, fontWeight: 700 }}>Decision History{originalCode ? `: ${originalCode}` : ''}</span>}
      footer={<Button onClick={onClose} data-telemetry-label="close_decision_history">Close</Button>}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 30 }}><Spin /></div>
      ) : history.length === 0 ? (
        <div style={{ color: C.textMuted, textAlign: 'center', padding: 20 }}>No history recorded yet</div>
      ) : (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {history.map((h, i) => {
            const style = actionStyles[h.action] || { color: '#94A3B8', icon: null };
            return (
              <div key={h.id || i} style={{
                display: 'flex', gap: 10, padding: '10px 0',
                borderBottom: i < history.length - 1 ? `1px solid ${C.border}` : 'none',
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  background: style.color, color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12,
                }}>
                  {style.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: style.color, textTransform: 'capitalize' }}>
                      {h.action.replace(/_/g, ' ')}
                    </span>
                    <span style={{ fontSize: 11, color: C.textMuted }}>
                      {h.performed_at ? new Date(h.performed_at).toLocaleString() : ''}
                    </span>
                  </div>
                  {h.previous_code && h.new_code && (
                    <div style={{ fontSize: 13, marginBottom: 3 }}>
                      <span style={{ textDecoration: 'line-through', color: '#94A3B8', fontFamily: 'monospace' }}>{h.previous_code}</span>
                      <span style={{ margin: '0 6px', color: C.textMuted }}>→</span>
                      <span style={{ fontWeight: 700, color: '#1D4ED8', fontFamily: 'monospace' }}>{h.new_code}</span>
                    </div>
                  )}
                  {h.note && (
                    <div style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic', lineHeight: 1.4 }}>
                      "{h.note}"
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: C.textMuted }}>by {h.performed_by}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};



// ── Chat popup ────────────────────────────────────────────────────────────────
export const ChatPopup = ({ messages, inputValue, onInputChange, onSend, loading }) => {
  const bottomRef = useRef(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Format context label for a message
  const formatContext = (ctx) => {
    if (!ctx) return null;
    const parts = [];
    if (ctx.encounter_number !== null && ctx.encounter_number !== undefined) {
      parts.push(`ENC ${String(ctx.encounter_number).padStart(2, '0')}`);
    }
    if (ctx.icd10_code) parts.push(ctx.icd10_code);
    return parts.length > 0 ? parts.join(' · ') : null;
  };

  return (
    <div style={{
      width: 500, height: 840,
      background: 'white', borderRadius: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px',
        background: C.navy, color: 'white',
        fontSize: 18, fontWeight: 700,
        borderRadius: '12px 12px 0 0',
      }}>
        HCC Assistant
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {messages.length === 0 && (
          <div style={{ color: C.textMuted, fontSize: 16, textAlign: 'center', marginTop: 20 }}>
            Ask anything about the selected code, evidence, or HCC guidelines.
          </div>
        )}
        {messages.map((msg, i) => {
          const ctxLabel = msg.role === 'user' ? formatContext(msg.context) : null;
          return (
            <div key={i} style={{ alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
              {ctxLabel && (
                <div style={{
                  fontSize: 11, fontWeight: 600, color: C.textMuted,
                  textAlign: 'right', marginBottom: 2, letterSpacing: '0.03em',
                }}>
                  {ctxLabel}
                </div>
              )}

              <div style={{
                background: msg.role === 'user' ? C.navy : '#F1F5F9',
                color: msg.role === 'user' ? 'white' : C.text,
                padding: '8px 12px', borderRadius: 8,
                fontSize: 14, lineHeight: 1.5,
              }}>
                {msg.role === 'assistant' ? (
                  <ReactMarkdown
                    components={{
                      p:  ({ children }) => <p style={{ margin: '4px 0' }}>{children}</p>,
                      strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
                      ul: ({ children }) => <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ul>,
                      ol: ({ children }) => <ol style={{ margin: '4px 0', paddingLeft: 18 }}>{children}</ol>,
                      li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
                      h1: ({ children }) => <div style={{ fontWeight: 700, fontSize: 16, margin: '6px 0 2px' }}>{children}</div>,
                      h2: ({ children }) => <div style={{ fontWeight: 700, fontSize: 15, margin: '6px 0 2px' }}>{children}</div>,
                      h3: ({ children }) => <div style={{ fontWeight: 700, fontSize: 14, margin: '4px 0 2px' }}>{children}</div>,
                      code: ({ children }) => (
                        <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 4px', borderRadius: 3, fontSize: 13 }}>
                          {children}
                        </code>
                      ),
                      pre: ({ children }) => (
                        <pre style={{ background: 'rgba(0,0,0,0.06)', padding: 8, borderRadius: 4, overflowX: 'auto', fontSize: 13, margin: '4px 0' }}>
                          {children}
                        </pre>
                      ),
                    }}
                  >
                    {msg.content}
                  </ReactMarkdown>
                ) : msg.content}
              </div>

            </div>
          );
        })}
        {loading && (
          <div style={{ alignSelf: 'flex-start', padding: '8px 12px', background: '#F1F5F9', borderRadius: 8 }}>
            <Spin size="small" />
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={{
        padding: '10px 12px', borderTop: `1px solid ${C.border}`,
        display: 'flex', gap: 8, alignItems: 'center',
      }}>
        <input
          value={inputValue}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          placeholder="Ask about this code..."
          style={{
            flex: 1, padding: '7px 10px', fontSize: 16,
            border: `1px solid ${C.border}`, borderRadius: 6,
            outline: 'none', color: C.text,
          }}
        />
        <button
          onClick={onSend}
          disabled={!inputValue.trim() || loading}
          style={{
            width: 34, height: 34, borderRadius: 6,
            background: !inputValue.trim() || loading ? '#E5E7EB' : C.navy,
            border: 'none', cursor: !inputValue.trim() || loading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <SendOutlined style={{ color: 'white', fontSize: 17 }} />
        </button>
      </div>
    </div>
  );
};


// ── P3 PDF viewer constants ───────────────────────────────────────────────────
export const EMPTY_HIGHLIGHTS_P3 = [];
export const EmptyHighlightContainerP3 = () => null;

// ── Text matching utilities for PDF evidence highlighting ─────────────────────

// Find match ranges in fullText using a 4-tier strategy.
// Returns Array<{start, end}> | null. Strategies 1, 2, 2b and 4 return a
// single-element array (contiguous match). Strategy 3 may return multiple
// disjoint ranges — this is what handles compacted bullet lists and
// multi-column metadata grids where PDF.js's text-layer reading order
// interleaves unrelated text between the evidence fragments.
export const findMatchInText = (fullText, matchText) => {
  const trimmed = matchText.trim();
  if (!trimmed) return null;

  // Strategy 1: exact
  const i1 = fullText.indexOf(trimmed);
  if (i1 !== -1) return [{ start: i1, end: i1 + trimmed.length }];

  // Strategy 2: normalise whitespace (handles newlines)
  const norm = s => s.replace(/\s+/g, ' ').trim();
  const normFull = norm(fullText);
  const normMatch = norm(trimmed);
  const i2 = normFull.indexOf(normMatch);
  if (i2 !== -1) return [{ start: i2, end: i2 + normMatch.length }];

  // Strategy 2b: whitespace-insensitive contiguous match
  // PDF.js spans joined without separators can drop whitespace at line breaks
  // (e.g. "(HCC)Not at goal" vs "(HCC) Not at goal"). Strip ALL whitespace
  // from both strings, match, then map indices back to the original fullText.
  const stripWs = s => s.replace(/\s+/g, '');
  {
    const strippedFull  = stripWs(fullText);
    const strippedMatch = stripWs(trimmed);
    const iWs = strippedFull.indexOf(strippedMatch);
    if (iWs !== -1) {
      // Map stripped index → original fullText index
      let origStart = 0, sIdx = 0;
      while (sIdx < iWs) {
        if (!/\s/.test(fullText[origStart])) sIdx++;
        origStart++;
      }
      // Skip any leading whitespace at the boundary
      while (origStart < fullText.length && /\s/.test(fullText[origStart])) origStart++;

      let origEnd = origStart, matched = 0;
      while (matched < strippedMatch.length && origEnd < fullText.length) {
        if (!/\s/.test(fullText[origEnd])) matched++;
        origEnd++;
      }
      return [{ start: origStart, end: origEnd }];
    }
  }

  // Strategy 3: split into fragments and locate each independently.
  // Splits on bullets (●, •, ▶, etc.), sentence-ish boundaries, and
  // multi-space gaps. Each fragment is matched exact-first then
  // whitespace-stripped, searching forward from the previous match so
  // ranges stay in document order. Disjoint ranges are preserved — this
  // is what stops irrelevant text between fragments from being painted.
  const fragments = trimmed
    .split(/[^\w\s'",.:;()\-\/+@#!?]+|(?<=[.!?:])\s+|\s{2,}/)
    .map(s => s.trim())
    .filter(s => s.length >= 4);

  if (fragments.length >= 2) {
    // Build a stripped-→-original index map once for fragment fallback
    const strippedFull = stripWs(fullText);
    const wsIndexMap = [];
    for (let i = 0; i < fullText.length; i++) {
      if (!/\s/.test(fullText[i])) wsIndexMap.push(i);
    }

    const findFragmentFrom = (frag, startFrom) => {
      // Try exact first
      const iE = fullText.indexOf(frag, startFrom);
      if (iE !== -1) return { start: iE, end: iE + frag.length };

      // Whitespace-stripped fallback
      const strippedFrag = stripWs(frag);
      if (!strippedFrag) return null;
      let strippedFrom = 0;
      while (strippedFrom < wsIndexMap.length && wsIndexMap[strippedFrom] < startFrom) {
        strippedFrom++;
      }
      const iWs = strippedFull.indexOf(strippedFrag, strippedFrom);
      if (iWs === -1) return null;
      const origStart = wsIndexMap[iWs];
      const origEndMap = wsIndexMap[iWs + strippedFrag.length - 1];
      if (origStart === undefined || origEndMap === undefined) return null;
      return { start: origStart, end: origEndMap + 1 };
    };

    const ranges = [];
    let from = 0;
    for (const frag of fragments) {
      const r = findFragmentFrom(frag, from);
      if (r) { ranges.push(r); from = r.end; }
    }
    // Require ≥2 fragments — otherwise fall through to strategy 3.5
    if (ranges.length >= 2) return ranges;
  }

  // Strategy 3.5: word-level token search.
  // Fallback for evidence text that's a concatenation of phrases pulled from
  // visually-distinct PDF regions with no boundary markers (e.g., metadata
  // grid cells stitched onto body paragraphs). Tokenises the evidence into
  // distinctive words (length ≥4), locates each in forward order, then
  // gap-merges nearby matches so words that visually belong together share
  // one range while disjoint phrases get separate ranges.
  {
    const tokens = trimmed
      .split(/\s+/)
      .map(t => t.replace(/^[^\w]+|[^\w]+$/g, ''))
      .filter(t => t.length >= 4);

    if (tokens.length >= 3) {
      const tokenRanges = [];
      let tFrom = 0;
      for (const tok of tokens) {
        const i = fullText.indexOf(tok, tFrom);
        if (i !== -1) {
          tokenRanges.push({ start: i, end: i + tok.length });
          tFrom = i + tok.length;
        }
      }

      if (tokenRanges.length >= 2) {
        // Merge ranges whose gap in fullText is small (≤80 chars). Tokens
        // closer than this are visually adjacent on the page; larger gaps
        // mean the evidence spans different regions of the PDF.
        const merged = [];
        const GAP = 80;
        for (const r of tokenRanges) {
          const last = merged[merged.length - 1];
          if (last && r.start - last.end <= GAP) {
            last.end = r.end;
          } else {
            merged.push({ start: r.start, end: r.end });
          }
        }
        // Coverage gate: require the matched tokens to account for at least
        // 30% of the evidence text length (capped at 15 chars), so we don't
        // paint stray noise when the real content is on a different page.
        const totalCovered = merged.reduce((sum, r) => sum + (r.end - r.start), 0);
        if (totalCovered >= Math.min(15, trimmed.length * 0.3)) {
          return merged;
        }
      }
    }
  }

  // Strategy 4: longest single fragment
  if (fragments.length >= 1) {
    const longest = fragments.reduce((a, b) => a.length > b.length ? a : b, '');
    if (longest.length >= 5) {
      const li = fullText.indexOf(longest);
      if (li !== -1) return [{ start: li, end: li + longest.length }];
    }
  }

  return null;
};

// Convert PDF text items + match range → react-pdf-highlighter Highlight object.
// Returns null on any failure — caller falls back to page navigation only.
export const computeHighlightPosition = async (pdfDoc, relPage, matchText) => {
  if (!pdfDoc || !matchText?.trim() || relPage < 1) return null;
  try {
    const page       = await pdfDoc.getPage(relPage);
    const viewport   = page.getViewport({ scale: 1 });
    const textContent= await page.getTextContent();
    const items      = textContent.items.filter(i => 'str' in i && i.str.length > 0);

    const fullText   = items.map(i => i.str).join('');
    const matches    = findMatchInText(fullText, matchText);
    if (!matches?.length) return null;
    const maxEnd     = Math.max(...matches.map(m => m.end));

    let cursor = 0;
    const matchedItems = [];
    for (const item of items) {
      const start = cursor, end = cursor + item.str.length;
      if (matches.some(m => end > m.start && start < m.end)) matchedItems.push(item);
      cursor = end;
      if (cursor > maxEnd) break;
    }
    if (!matchedItems.length) return null;

    const rects = matchedItems.map(item => {
      const [, , , d, tx, ty] = item.transform;
      const h  = Math.abs(d) || 10;
      const [x1, y1, x2, y2] = viewport.convertToViewportRectangle([
        tx, ty - h, tx + (item.width || 10), ty,
      ]);
      return {
        x1: Math.min(x1, x2), y1: Math.min(y1, y2),
        x2: Math.max(x1, x2), y2: Math.max(y1, y2),
        width: viewport.width, height: viewport.height,
      };
    });

    const boundingRect = {
      x1: Math.min(...rects.map(r => r.x1)),
      y1: Math.min(...rects.map(r => r.y1)),
      x2: Math.max(...rects.map(r => r.x2)),
      y2: Math.max(...rects.map(r => r.y2)),
      width: viewport.width, height: viewport.height,
    };

    return {
      id:       `ev-hl-${relPage}-${Date.now()}`,
      content:  { text: matchText.trim() },
      position: { pageNumber: relPage, boundingRect, rects },
    };
  } catch (err) {
    console.warn('Highlight computation failed:', err);
    return null;
  }
};

// ── HighlightContainer for evidence highlights in P3 ─────────────────────────
export const EvidenceHighlightContainerP3 = () => {
  const { highlight, isScrolledTo } = useHighlightContainerContext();
  return (
    <TextHighlight
      isScrolledTo={isScrolledTo}
      highlight={highlight}
      style={{ background: 'rgba(255, 220, 0, 0.55)', mixBlendMode: 'multiply' }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// P3SelectionTip
// Rendered by PdfHighlighter inside P3 as selectionTip.
// Reads selection via usePdfHighlighterContext and shows a floating
// "Add Support for {code}" button. Uses selectedFindingRef (not prop) so
// the tip always shows the current code without causing PdfHighlighter re-renders.
// ─────────────────────────────────────────────────────────────────────────────
export const P3SelectionTip = ({ startPage, onAddSupport, findingRef }) => {
  const { getCurrentSelection } = usePdfHighlighterContext();
  const lastTextRef = useRef('');
  const [sel, setSel] = useState(null);

  useEffect(() => {
    const s = getCurrentSelection?.();
    const text = s?.content?.text?.trim();
    if (text && text !== lastTextRef.current) {
      lastTextRef.current = text;
      setSel({
        text,
        absolutePage: (startPage ?? 1) + (s.position?.pageNumber ?? 1) - 1,
      });
    }
  });

  if (!sel || !onAddSupport) return null;
  const finding = findingRef.current;

  return (
    <div style={{
      background: '#162349', color: 'white',
      borderRadius: 6, padding: '10px 14px',
      boxShadow: '0 4px 18px rgba(0,0,0,0.3)',
      display: 'flex', flexDirection: 'column', gap: 8,
      maxWidth: 260, fontSize: 13,
    }}>
      <div style={{
        fontSize: 11, color: 'rgba(255,255,255,0.6)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        "{sel.text.length > 50 ? sel.text.slice(0, 50) + '…' : sel.text}"
      </div>
      {finding ? (
        <button
          onClick={() => { onAddSupport(sel); lastTextRef.current = ''; setSel(null); }}
          style={{
            background: '#92A372', color: 'white', border: 'none',
            borderRadius: 4, padding: '6px 12px',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            letterSpacing: '0.03em',
          }}
        >
          + Add Support for {finding.icd10_code}
        </button>
      ) : (
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
          Select a code in the code list first
        </span>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// P3PdfViewer — React.memo
// Renders the encounter PDF using react-pdf-highlighter-extended.
// Stays mounted across finding selections (only reloads when encounter changes).
// Scrolls to activePdfPage whenever it changes.
// ─────────────────────────────────────────────────────────────────────────────
export const P3PdfViewer = React.memo(({
  encounterPdfParams,  // memoized { url, httpHeaders } — stable per encounter
  startPage,           // encounter start_page for page calculations
  endPage,             // encounter end_page — shown in page indicator
  activePdfPage,       // absolute chart page to scroll to
  evidenceItem,        // { matchText, absolutePage, section } | null — drives highlight
  onAddSupport,        // stable callback
  findingRef,          // ref to selectedFinding — never changes reference
}) => {
  const containerRef     = useRef(null);
  const [currentAbsPage, setCurrentAbsPage] = useState(null);

  // ── In-PDF text search state ───────────────────────────────────────────────
  const [searchQuery,     setSearchQuery]     = useState('');
  const [searchMatches,   setSearchMatches]   = useState([]); // [{ pageEl, spans, pageNum }]
  const [currentMatchIdx, setCurrentMatchIdx] = useState(0);
  const searchInputRef = useRef(null);

  // ── Scroll-based page indicator ────────────────────────────────────────────
  // PdfHighlighter renders its own internal scrollable div — scroll events
  // don't bubble, so we need capture:true on our container AND a periodic
  // interval as fallback for the initial render.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateIndicator = () => {
      // Query page elements from the inner pdfViewer rendered by PDF.js
      const pages = container.querySelectorAll('[data-page-number]');
      if (!pages.length) return;
      // Use the inner scrollable element if available, else the container
      const scroller = container.querySelector('.PdfHighlighter')
                    || container.querySelector('[style*="overflow"]')
                    || container;
      const cRect = scroller.getBoundingClientRect();
      let bestPage = null, bestVisible = -1;
      pages.forEach(page => {
        const r    = page.getBoundingClientRect();
        const visH = Math.max(0, Math.min(r.bottom, cRect.bottom) - Math.max(r.top, cRect.top));
        if (visH > bestVisible) { bestVisible = visH; bestPage = page; }
      });
      if (bestPage) {
        const rel = parseInt(bestPage.getAttribute('data-page-number'), 10);
        if (!isNaN(rel)) setCurrentAbsPage(startPage + rel - 1);
      }
    };

    // capture:true catches scroll from any descendant (PdfHighlighter's inner div)
    container.addEventListener('scroll', updateIndicator, { capture: true, passive: true });

    // Interval fallback — polls every 400ms until pages are found, then stops
    // polling but keeps checking periodically for scroll position changes
    let intervalCount = 0;
    const interval = setInterval(() => {
      updateIndicator();
      intervalCount++;
      // After 10s of polling, switch to slower 2s interval
      if (intervalCount > 25) clearInterval(interval);
    }, 400);

    return () => {
      container.removeEventListener('scroll', updateIndicator, { capture: true });
      clearInterval(interval);
    };
  }, [encounterPdfParams, startPage]); // reset when encounter changes

  // ── Scroll to target page ──────────────────────────────────────────────────
  // Retries up to 15× at 500ms intervals (~7.5s window) to handle fresh PDF
  // loads where pages aren't in the DOM yet when the scroll is first requested.
  const scrollToPage = useCallback((absPage) => {
    if (!absPage || !startPage) return;
    const relPage = absPage - startPage + 1;
    let cancelled = false;

    const attempt = (tries) => {
      if (cancelled || tries > 15) return;
      const viewer = containerRef.current?.querySelector('.pdfViewer');
      const target = viewer?.querySelector(`[data-page-number="${relPage}"]`);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        setTimeout(() => attempt(tries + 1), 500);
      }
    };

    setTimeout(() => attempt(0), 300);
    return () => { cancelled = true; };
  }, [startPage]);

  useEffect(() => {
    return scrollToPage(activePdfPage);
  }, [activePdfPage, encounterPdfParams, scrollToPage]);

  // ── DOM-based evidence highlight ──────────────────────────────────────────
  // Appends absolutely-positioned yellow divs over matching text layer spans.
  // getBoundingClientRect() gives exact rendered positions — no coordinate math.
  const doHighlight = useCallback((item) => {
    const root = containerRef.current;
    if (!root) return;

    // Clear previous overlays
    root.querySelectorAll('.ev-hl-overlay').forEach(el => el.remove());
    if (!item?.matchText || !item.absolutePage) return;

    const relPage = item.absolutePage - startPage + 1;

    // Apply highlight overlays on a given page element using the match result
    const applyOverlays = (pageEl, spans, matches) => {
      if (!matches?.length) return false;
      const maxEnd = Math.max(...matches.map(m => m.end));
      let cursor = 0;
      const matchingSpans = [];
      for (const span of spans) {
        const len = span.textContent.length;
        const s = cursor, e = cursor + len;
        if (matches.some(m => e > m.start && s < m.end)) matchingSpans.push(span);
        cursor = e;
        if (cursor > maxEnd) break;
      }
      if (!matchingSpans.length) return false;

      const pageRect = pageEl.getBoundingClientRect();
      matchingSpans.forEach(span => {
        const r = span.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return;
        const div = document.createElement('div');
        div.className = 'ev-hl-overlay';
        Object.assign(div.style, {
          position:       'absolute',
          left:           `${r.left - pageRect.left}px`,
          top:            `${r.top  - pageRect.top}px`,
          width:          `${Math.max(r.width,  4)}px`,
          height:         `${Math.max(r.height, 10)}px`,
          background:     'rgba(255, 220, 0, 0.55)',
          pointerEvents:  'none',
          borderRadius:   '2px',
          zIndex:         '5',
          mixBlendMode:   'multiply',
        });
        pageEl.appendChild(div);
      });
      return true;
    };

    // Try to find and highlight match on a specific relative page number
    const tryPage = (rp) => {
      const pageEl    = root.querySelector(`[data-page-number="${rp}"]`);
      const textLayer = pageEl?.querySelector('.textLayer');
      const spans     = textLayer ? Array.from(textLayer.querySelectorAll('span')) : [];
      if (!pageEl || !textLayer || !spans.length) return null; // page not rendered
      const fullText = spans.map(s => s.textContent).join('');
      const matches  = findMatchInText(fullText, item.matchText);
      if (!matches?.length) return false; // page rendered but text not found
      applyOverlays(pageEl, spans, matches);
      return true; // highlighted
    };

    const attempt = (tries = 0) => {
      const result = tryPage(relPage);

      // Target page not rendered yet — retry
      if (result === null) {
        if (tries < 20) setTimeout(() => attempt(tries + 1), 500);
        return;
      }

      // Match found on target page — done
      if (result === true) return;

      // Match NOT found on target page — look ahead/behind.
      // Evidence often references the section boundary page, but the actual
      // text can spill onto the following page.
      let adjacentPending = false;

      const nextResult = tryPage(relPage + 1);
      if (nextResult === true) {
        const nextPageEl = root.querySelector(`[data-page-number="${relPage + 1}"]`);
        if (nextPageEl) nextPageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (nextResult === null) adjacentPending = true;

      if (relPage > 1) {
        const prevResult = tryPage(relPage - 1);
        if (prevResult === true) {
          const prevPageEl = root.querySelector(`[data-page-number="${relPage - 1}"]`);
          if (prevPageEl) prevPageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (prevResult === null) adjacentPending = true;
      }

      // Adjacent pages not rendered yet — retry to give PDF.js time
      // to render textLayers for pages near the viewport
      if (adjacentPending && tries < 20) {
        setTimeout(() => attempt(tries + 1), 500);
      }
    };

    setTimeout(() => attempt(), 800); // delay — let scrollToPage fire and settle first
  }, [startPage]);

  // Re-highlight whenever evidenceItem changes
  useEffect(() => {
    doHighlight(evidenceItem);
  }, [evidenceItem, doHighlight]);

  // Clear overlays when encounter changes (new PDF loads)
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    root.querySelectorAll('.ev-hl-overlay').forEach(el => el.remove());
    root.querySelectorAll('.pdf-search-overlay').forEach(el => el.remove());
    setSearchQuery('');
    setSearchMatches([]);
    setCurrentMatchIdx(0);
  }, [encounterPdfParams]);

  // ── In-PDF search: scan all currently-rendered pages for the query ─────────
  // Paints a `.pdf-search-overlay` div over each matching span (orange tint).
  // The "current" match gets a brighter shade applied by a separate effect.
  const runSearch = useCallback((rawQuery) => {
    const root = containerRef.current;
    if (!root) return;

    // Clear previous search overlays (does not touch evidence highlights)
    root.querySelectorAll('.pdf-search-overlay').forEach(el => el.remove());

    const q = (rawQuery || '').trim().toLowerCase();
    if (q.length < 2) {
      setSearchMatches([]);
      setCurrentMatchIdx(0);
      return;
    }

    const allMatches = [];
    const pages = root.querySelectorAll('[data-page-number]');

    pages.forEach((pageEl) => {
      const textLayer = pageEl.querySelector('.textLayer');
      if (!textLayer) return; // page not yet rendered
      const spans = Array.from(textLayer.querySelectorAll('span'));
      if (!spans.length) return;

      const fullText = spans.map(s => s.textContent).join('').toLowerCase();
      const pageNum  = parseInt(pageEl.getAttribute('data-page-number'), 10);

      // Find ALL occurrences of q in this page's text
      let from = 0;
      while (from < fullText.length) {
        const idx = fullText.indexOf(q, from);
        if (idx === -1) break;
        const matchEnd = idx + q.length;

        // Walk spans, collect those that overlap [idx, matchEnd)
        let cursor = 0;
        const matchSpans = [];
        for (const span of spans) {
          const len = span.textContent.length;
          const s = cursor, e = cursor + len;
          if (e > idx && s < matchEnd) matchSpans.push(span);
          cursor = e;
          if (cursor > matchEnd) break;
        }
        if (matchSpans.length) {
          allMatches.push({ pageEl, spans: matchSpans, pageNum });
        }
        from = matchEnd;
      }
    });

    // Paint overlays
    const pageRectCache = new Map();
    allMatches.forEach((m, mIdx) => {
      let pageRect = pageRectCache.get(m.pageEl);
      if (!pageRect) {
        pageRect = m.pageEl.getBoundingClientRect();
        pageRectCache.set(m.pageEl, pageRect);
      }
      m.spans.forEach(span => {
        const r = span.getBoundingClientRect();
        const div = document.createElement('div');
        div.className = 'pdf-search-overlay';
        div.dataset.matchIdx = String(mIdx);
        Object.assign(div.style, {
          position:   'absolute',
          left:       `${r.left - pageRect.left}px`,
          top:        `${r.top  - pageRect.top}px`,
          width:      `${r.width}px`,
          height:     `${r.height}px`,
          background: 'rgba(255, 165, 0, 0.35)', // dim orange for non-current
          mixBlendMode: 'multiply',
          pointerEvents: 'none',
          zIndex: 5,
        });
        m.pageEl.appendChild(div);
      });
    });

    setSearchMatches(allMatches);
    setCurrentMatchIdx(allMatches.length ? 0 : 0);
  }, []);

  // Debounced re-run on query change (200ms — lets users type without thrash)
  useEffect(() => {
    const t = setTimeout(() => runSearch(searchQuery), 200);
    return () => clearTimeout(t);
  }, [searchQuery, runSearch]);

  // Re-scan when new pages render (PDF.js lazy-renders pages on scroll).
  // MutationObserver fires when textLayer spans appear in a new page.
  useEffect(() => {
    if (!searchQuery) return;
    const root = containerRef.current;
    if (!root) return;
    let debounce = null;
    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some(m =>
        Array.from(m.addedNodes).some(n =>
          n.nodeType === 1 && (
            n.classList?.contains('textLayer') ||
            n.querySelector?.('.textLayer')
          )
        )
      );
      if (!relevant) return;
      clearTimeout(debounce);
      debounce = setTimeout(() => runSearch(searchQuery), 300);
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      clearTimeout(debounce);
      observer.disconnect();
    };
  }, [searchQuery, runSearch]);

  // Highlight the current match brighter + scroll into view
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !searchMatches.length) return;
    root.querySelectorAll('.pdf-search-overlay').forEach(el => {
      const isActive = el.dataset.matchIdx === String(currentMatchIdx);
      el.style.background = isActive
        ? 'rgba(255, 140, 0, 0.65)'     // bright orange — active match
        : 'rgba(255, 200, 90, 0.32)';   // dim — other matches
    });
    const active = searchMatches[currentMatchIdx];
    if (active?.spans?.[0]) {
      active.spans[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentMatchIdx, searchMatches]);

  // Navigation helpers
  const gotoNextMatch = useCallback(() => {
    setCurrentMatchIdx(i => searchMatches.length ? (i + 1) % searchMatches.length : 0);
  }, [searchMatches.length]);

  const gotoPrevMatch = useCallback(() => {
    setCurrentMatchIdx(i => searchMatches.length
      ? (i - 1 + searchMatches.length) % searchMatches.length
      : 0);
  }, [searchMatches.length]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    const root = containerRef.current;
    if (root) root.querySelectorAll('.pdf-search-overlay').forEach(el => el.remove());
    setSearchMatches([]);
    setCurrentMatchIdx(0);
  }, []);

  // Keyboard shortcuts: ⌘F focuses input, Esc clears
  useEffect(() => {
    const onKey = (e) => {
      // Only intercept ⌘F / Ctrl+F when P3 is in the user's focus context.
      // We scope it to the P3 container — Cmd+F still works elsewhere.
      const root = containerRef.current;
      if (!root) return;
      const inP3 = root.contains(document.activeElement) || root.matches(':hover');
      if ((e.metaKey || e.ctrlKey) && (e.key === 'f' || e.key === 'F') && inP3) {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
      if (e.key === 'Escape' && document.activeElement === searchInputRef.current) {
        clearSearch();
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clearSearch]);

  return (
    // Outer wrapper — position:relative so the badge can be position:absolute
    <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>

      {/* ── Floating in-PDF search bar (top-left) ── */}
      {encounterPdfParams && (
        <div
          style={{
            position: 'absolute', top: 10, left: 14,
            display: 'flex', alignItems: 'center', gap: 4,
            background: 'white',
            border: '1px solid #CBD5E1',
            borderRadius: 8,
            padding: '3px 6px 3px 10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            zIndex: 25,
            minHeight: 32,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <SearchOutlined style={{ color: '#64748B', fontSize: 14, marginRight: 2 }} />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (e.shiftKey) gotoPrevMatch();
                else gotoNextMatch();
              }
            }}
            placeholder="Search PDF…"
            style={{
              border: 'none', outline: 'none',
              fontSize: 13, color: '#0F172A',
              width: 180, padding: '2px 4px',
              background: 'transparent',
            }}
          />
          {searchQuery && (
            <>
              <span style={{
                fontSize: 11, color: searchMatches.length ? '#475569' : '#94A3B8',
                fontVariantNumeric: 'tabular-nums',
                padding: '0 6px',
                whiteSpace: 'nowrap',
                minWidth: 44, textAlign: 'right',
              }}>
                {searchMatches.length
                  ? `${currentMatchIdx + 1} / ${searchMatches.length}`
                  : '0 / 0'}
              </span>
              <Tooltip title="Previous (Shift+Enter)">
                <Button
                  size="small" type="text"
                  icon={<UpOutlined style={{ fontSize: 11 }} />}
                  onClick={gotoPrevMatch}
                  disabled={!searchMatches.length}
                  data-telemetry-label="pdf_search_prev"
                  style={{ width: 22, height: 22, padding: 0 }}
                />
              </Tooltip>
              <Tooltip title="Next (Enter)">
                <Button
                  size="small" type="text"
                  icon={<DownOutlined style={{ fontSize: 11 }} />}
                  onClick={gotoNextMatch}
                  disabled={!searchMatches.length}
                  data-telemetry-label="pdf_search_next"
                  style={{ width: 22, height: 22, padding: 0 }}
                />
              </Tooltip>
              <Tooltip title="Clear (Esc)">
                <Button
                  size="small" type="text"
                  icon={<CloseOutlined style={{ fontSize: 11 }} />}
                  onClick={clearSearch}
                  data-telemetry-label="pdf_search_clear"
                  style={{ width: 22, height: 22, padding: 0 }}
                />
              </Tooltip>
            </>
          )}
        </div>
      )}

      {/* Scrollable PDF container */}
      <div
        ref={containerRef}
        style={{ height: '100%', overflow: 'auto', position: 'relative', background: '#F1F5F9' }}
      >
        {!encounterPdfParams ? (
          <div style={{
            height: '100%', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            <FilePdfOutlined style={{ fontSize: 40, color: '#CBD5E1' }} />
            <span style={{ fontSize: 16, color: '#64748B' }}>
              Select an encounter to view the PDF
            </span>
          </div>
        ) : (
          <PdfLoader 
            document={encounterPdfParams}
            beforeLoad={() => (
              <div style={{
                height: '100%', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 12,
                background: 'white',
              }}>
                <Spin size="large" />
                <span style={{ fontSize: 15, color: '#64748B' }}>Loading PDF…</span>
              </div>
            )}
            >
            {(pdfDocument) => (
              <PdfHighlighter
                pdfDocument={pdfDocument}
                highlights={EMPTY_HIGHLIGHTS_P3}
                enableAreaSelection={() => false}
                utilsRef={(_utils) => {}}
                selectionTip={
                  <P3SelectionTip
                    startPage={startPage}
                    onAddSupport={onAddSupport}
                    findingRef={findingRef}
                  />
                }
              >
                <EmptyHighlightContainerP3 />
              </PdfHighlighter>
            )}
          </PdfLoader>
        )}
      </div>

      {/* ── Floating page indicator ── */}
      {currentAbsPage && (
        <div style={{
          position: 'absolute', top: 10, right: 14,
          background: 'rgba(22, 35, 73, 0.82)',
          backdropFilter: 'blur(4px)',
          color: 'white', borderRadius: 6,
          padding: '4px 12px', fontSize: 13, fontWeight: 700,
          letterSpacing: '0.04em', pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          userSelect: 'none',
          zIndex: 20,
        }}>
          p.{currentAbsPage}
          {endPage ? (
            <span style={{ opacity: 0.55, fontWeight: 400, marginLeft: 2 }}>
              {' '}/ {endPage}
            </span>
          ) : null}
        </div>
      )}

    </div>
  );
});